import * as Select from "@radix-ui/react-select";
import { forwardRef, JSX, useMemo, useState } from "react";

import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon } from "../../Icon";
import { SELECTED_MENU_CLASS_NAME } from "./const";
import { FilterableMultiSelectProps } from "./type";
import { IndeterminateCheckBox } from "./ui/IndeterminateCheckBox";
import { SelectValue } from "./ui/SelectValue";
import { useFilterableMultiSelect } from "./useFilterableMultiSelect";

export const FilterableMultiSelect = forwardRef<HTMLDivElement, FilterableMultiSelectProps>(
  ({ value, onChange, options }, forwardedRef) => {
    const [open, setOpen] = useState(false);

    const { selectedPath, handleOptionSelect, handleValueChange, isChecked, getValueLabel } = useFilterableMultiSelect({
      value,
      onChange,
      options,
    });

    // レンダリングする階層
    const renderColumns = useMemo(() => {
      const columns: JSX.Element[] = [];
      let currentOptions = options;

      for (let level = 0; level <= selectedPath.length; level++) {
        columns.push(
          <div key={level} className="border-grayScale-200 max-h-[240px] min-w-[150px] overflow-y-auto border-r">
            {currentOptions.map((option) => (
              <div
                key={option.value}
                className={SELECTED_MENU_CLASS_NAME({
                  budgeType: selectedPath[level]?.value === option.value && option.options ? "active" : "none",
                })}
                onClick={() => handleOptionSelect(option, level)}
              >
                <div>
                  <IndeterminateCheckBox onChange={() => handleValueChange(option)} checked={isChecked(option)} />
                  <span onClick={() => handleOptionSelect(option, level)}>{option.label}</span>
                </div>
                {option.options && option.options.length > 0 && <ChevronRightIcon className="fill-grayScale-300" />}
              </div>
            ))}
          </div>,
        );

        // 次の階層に移動
        const selectedOption = selectedPath[level];
        if (selectedOption?.options) {
          currentOptions = selectedOption.options;
        } else {
          break;
        }
      }

      return columns;
    }, [options, selectedPath, handleOptionSelect, handleValueChange, isChecked]);

    return (
      <Select.Root open={open} onOpenChange={setOpen}>
        <div
          className="bg-base-0 border-ui-200 flex w-full flex-row items-center justify-between rounded border p-2"
          ref={forwardedRef}
          onClick={() => setOpen(!open)}
        >
          <div className="flex flex-row flex-wrap gap-2">
            {value.length
              ? value.map((v) => (
                  <SelectValue
                    key={v}
                    label={getValueLabel(v)}
                    value={v}
                    onDelete={(v) => {
                      onChange(value.filter((item) => item !== v));
                    }}
                  />
                ))
              : "選択してください"}
          </div>
          <Select.Icon>
            {open ? (
              <ChevronUpIcon className="fill-grayScale-300" />
            ) : (
              <ChevronDownIcon className="fill-grayScale-300" />
            )}
          </Select.Icon>
        </div>
        <Select.Content className="z-10 mt-2 shadow-[0_12px_32px_0_rgba(0,0,0,0.12)]">
          <Select.Viewport>
            <div className="bg-base-0 flex flex-row rounded-md p-4 ">{renderColumns}</div>
          </Select.Viewport>
        </Select.Content>
      </Select.Root>
    );
  },
);

FilterableMultiSelect.displayName = "FilterableMultiSelect";
